import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConnectIcon } from 'assets/icons';
import ProfileCard from 'components/Page/Header/TopHeader/Profile/ProfileCard';
import { SPopover } from 'components/Page/Header/TopHeader/TopHeader.styled';
import { getActiveSession } from 'domains/delegation';
import { getTokenUser, getUserCommercialLink } from 'domains/user';
import { theme } from 'styles';
import { Flex, Icon, MarginBox, Text, WithTooltip } from 'UI';
import { Bubble } from './UserInfo.styled';

export const fmt = (name?: string, tresor?: string) => [name, tresor].filter(Boolean).join(' - ');

const UserInfo = () => {
  const { t } = useTranslation();
  const [profileVisible, setProfileVisible] = useState<boolean>(false);
  const userInfo = useSelector(getTokenUser);
  const commercialLink = useSelector(getUserCommercialLink);
  const activeSession = useSelector(getActiveSession);
  const isConnectedAs = Boolean(activeSession?.active);
  const id = userInfo?.selectedProfile?.id ?? '';
  const garageName = commercialLink?.name ?? '';
  const dealerTresorCode = commercialLink?.seller?.tresorCode ?? '';
  const connectedAsTresorCode = activeSession?.delegatedBuyerTresorCode ?? '';
  const connectedAsGarageName = fmt(
    activeSession?.delegatedBuyerName ?? '',
    activeSession?.delegatedBuyerTresorCode ?? '',
  );
  const firstName = userInfo?.firstName ?? '';
  const lastName = userInfo?.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const connectedAsLabel = t('header.connected_as ', 'Connected as');

  const getLabelPart = (label: string) => {
    if (label.trim() === '') {
      return '';
    }
    return `- ${label}`;
  };

  const getLabel = () => {
    if (isConnectedAs) {
      return `${connectedAsLabel} ${id} ${getLabelPart(connectedAsGarageName)}`;
    }
    return `${id} ${getLabelPart(garageName)} ${getLabelPart(fullName)}`;
  };

  const renderUserInfoColumns = () => {
    return (
      <>
        <Flex size={0} shrink={0}>
          <Text type={'text_white'}>{id}</Text>
        </Flex>
        <Flex overflowX={'hidden'}>
          <Text type={'text_white'} ellipsis>
            {getLabelPart(dealerTresorCode)}
          </Text>
        </Flex>
      </>
    );
  };

  const renderConnectedAsColumns = () => {
    return (
      <>
        <Flex size={0} shrink={0}>
          <Text type={'text_white'}>{connectedAsLabel}</Text>
        </Flex>
        <Flex size={0} shrink={0}>
          <Text type={'text_white'}>{id}</Text>
        </Flex>
        <Flex overflowX={'hidden'}>
          <Text type={'text_white'} ellipsis>
            {getLabelPart(connectedAsTresorCode)}
          </Text>
        </Flex>
      </>
    );
  };

  const getIcon = () => {
    if (isConnectedAs) {
      return (
        <Icon
          IconComponent={ConnectIcon}
          background={theme.color.red}
          round
          size={32}
          color={theme.color.white}
          display={'block'}
        />
      );
    }
    return <MarginBox ml={5} />;
  };

  return (
    <SPopover
      id={'top_header-profile-popover'}
      getPopupContainer={(trigger) => trigger.parentElement ?? trigger}
      content={<ProfileCard setProfileVisible={setProfileVisible} visible={profileVisible} />}
      trigger={'click'}
      placement={'bottomRight'}
      visible={profileVisible}
      onVisibleChange={(visible) => setProfileVisible(visible)}
      data-cy={'top-link-user-info'}
    >
      <Bubble direction={'row'} align={'center'} maxWidth={200}>
        {getIcon()}
        <MarginBox mr={10} ml={5}>
          <WithTooltip title={getLabel()} placement={'bottom'}>
            <Flex maxWidth={200} gap={isConnectedAs ? 4 : 0} overflowX={'hidden'}>
              {isConnectedAs ? renderConnectedAsColumns() : renderUserInfoColumns()}
            </Flex>
          </WithTooltip>
        </MarginBox>
      </Bubble>
    </SPopover>
  );
};

export default UserInfo;
